import React from 'react'
import { useTranslation } from 'react-i18next'
import { getSelectCountryFilter } from 'components/react-select/CountryFunctions'
import {
  FieldPath, FieldPathByValue, FieldValues,
} from 'react-hook-form'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import E1102Validator from 'features/declaration/form/validations/E1102'
import EoriSearch from 'features/declaration/form/components/EoriSearch'
import TraderSearch from 'features/declaration/form/components/TraderSearch'
import {
  ADDRESS_LINE_MAX_LENGTH,
  ADDRESS_STREET_MAX_LENGTH,
  ADDRESS_STREET_NUMBER_MAX_LENGTH,
  TRANSIT_PARTY_NAME_MAX_LENGTH,
} from 'shared/ncts/common/constants'
import { ConsigneeFormValues as ConsigneeType, TraderType } from 'features/declaration/models/schemas/tradersSchema'
import TypeOfPerson from 'features/declaration/form/components/TypeOfPerson'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import DotLeafPaths from '../../../../../helpers/dot-leaf-paths'
import Input from '../../../../../components/p5/Input'
import FormSelect from '../../../../../components/p5/FormSelect'
import FormField from '../../../../../components/p5/FormField'
import FormCollapsibleColumn from '../../../../../components/p5/FormCollapsibleColumn'
import { NctsScope } from '../../../../../shared/ncts/types'
import useDeclarationFieldRequirements
  from '../useDeclarationFieldRequirements'

type ConsigneeRelPath = DotLeafPaths<ConsigneeType>

type TraderBasePath<TForm extends FieldValues> = FieldPathByValue<TForm, TraderType | null | undefined>

interface ConsigneeProps<TForm extends FieldValues> {
  scope: TraderBasePath<TForm>
  nctsScope: NctsScope
  initialIsSectionOpen?: boolean
}

function Consignee<TForm extends FieldValues>({
  scope,
  initialIsSectionOpen,
  nctsScope,
}: Readonly<ConsigneeProps<TForm>>) {
  const { t } = useTranslation()
  const [, countriesOptions] = useCodelist('COUNTRIES')

  const field = (rel: ConsigneeRelPath) => `${scope}.${rel}` as FieldPath<TForm>

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>(nctsScope, [
    'consignee',
    'consignee.identificationNumber',
    'consignee.name',
    'consignee.typeOfPerson',
    'consignee.address',
    'consignee.address.country',
    'consignee.address.addressLine',
    'consignee.address.street',
    'consignee.address.streetNumber',
    'consignee.address.city',
    'consignee.address.postcode',
    'consignee.contactPerson',
    'consignee.contactPerson.name',
    'consignee.contactPerson.telephone',
    'consignee.contactPerson.email',
  ])

  const isAddressLineForbidden = fieldRequirements['consignee.address.addressLine']?.requirement === 'forbidden'
  const isAddressStreetForbidden = fieldRequirements['consignee.address.street']?.requirement === 'forbidden'
  const isShowAddressLine = !isAddressLineForbidden || isAddressStreetForbidden

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-consignee"
        headingLabel={t('declaration.p5.consignee')}
        initialIsSectionOpen={initialIsSectionOpen}
        fieldRule={fieldRequirements.consignee}
      >
        <FormField
          labelKey="declaration.p5.consigneeIdentificationNr"
          fieldRule={fieldRequirements['consignee.identificationNumber']}
        >
          <EoriSearch<TForm, typeof scope>
            scope={scope}
            traderTarget="CONSIGNEE"
            fieldRule={fieldRequirements['consignee.identificationNumber']}
          />
        </FormField>
        <FormField
          scope={`${scope}.name`}
          labelKey="declaration.p5.consigneeName"
          fieldRule={fieldRequirements['consignee.name']}
        >
          <TraderSearch
            scope={scope}
            maxLength={TRANSIT_PARTY_NAME_MAX_LENGTH}
            traderTarget="CONSIGNEE"
            fieldRule={fieldRequirements['consignee.name']}
          />
        </FormField>
        <TypeOfPerson<TForm> scope={scope} fieldRule={fieldRequirements['consignee.typeOfPerson']} />
        <FormField
          labelKey="declaration.p5.country"
          fieldRule={fieldRequirements['consignee.address.country']}
        >
          <FormSelect
            field={field('address.country')}
            labelKey="declaration.p5.country"
            options={countriesOptions}
            customFilterOption={getSelectCountryFilter}
            type="sync"
            fieldRule={fieldRequirements['consignee.address.country']}
          />
        </FormField>
        <div className="row party-address-row">
          <FormField
            className="col-12"
            labelKey="declaration.p5.addressLine"
            fieldRule={fieldRequirements['consignee.address.addressLine']}
            hidden={!isShowAddressLine}
          >
            <Input
              field={field('address.addressLine')}
              autoComplete={`${scope}.address.addressLine`}
              labelKey="declaration.p5.addressLine"
              maxLength={ADDRESS_LINE_MAX_LENGTH}
              type="text"
              placeholder={t('declaration.p5.addressLine')}
              fieldRule={fieldRequirements['consignee.address.addressLine']}
            />
          </FormField>
          <FormField
            className="col-6"
            labelKey="declaration.p5.street"
            fieldRule={fieldRequirements['consignee.address.street']}
            hidden={isShowAddressLine}
          >
            <Input
              field={field('address.street')}
              autoComplete="declaration.p5.street"
              labelKey="declaration.p5.street"
              maxLength={ADDRESS_STREET_MAX_LENGTH}
              type="text"
              placeholder={t('declaration.p5.street')}
              fieldRule={fieldRequirements['consignee.address.street']}
            />
          </FormField>
          <FormField
            className="col-6"
            labelKey="declaration.p5.streetNumber"
            fieldRule={fieldRequirements['consignee.address.streetNumber']}
            hidden={isShowAddressLine}
          >
            <Input
              field={field('address.streetNumber')}
              autoComplete={`${scope}.address.streetNumber`}
              labelKey="declaration.p5.streetNumber"
              maxLength={ADDRESS_STREET_NUMBER_MAX_LENGTH}
              type="text"
              placeholder={t('declaration.p5.streetNumber')}
              fieldRule={fieldRequirements['consignee.address.streetNumber']}
            />
          </FormField>
        </div>
        <div className="row party-address-row">
          <FormField
            className="col-8"
            labelKey="declaration.p5.city"
            fieldRule={fieldRequirements['consignee.address.city']}
          >
            <Input
              field={field('address.city')}
              autoComplete={`${scope}.address.city`}
              labelKey="declaration.p5.city"
              maxLength={35}
              type="text"
              placeholder={t('declaration.p5.city')}
              fieldRule={fieldRequirements['consignee.address.city']}
            />
          </FormField>
          <FormField
            className="col-4"
            labelKey="declaration.p5.postcode"
            fieldRule={fieldRequirements['consignee.address.postcode']}
          >
            <Input
              field={field('address.postcode')}
              autoComplete={`${scope}.address.postcode`}
              labelKey="declaration.p5.postcode"
              maxLength={E1102Validator.getPostcodeMaxLength()}
              type="text"
              placeholder={t('declaration.p5.postcode')}
              fieldRule={fieldRequirements['consignee.address.postcode']}
            />
          </FormField>
        </div>
        <FormField
          labelKey="declaration.p5.contactPersonName"
          fieldRule={fieldRequirements['consignee.contactPerson.name']}
        >
          <Input
            field={field('contactPerson.name')}
            autoComplete={`${scope}.contactPerson.name`}
            labelKey="declaration.p5.contactPersonName"
            maxLength={70}
            type="text"
            placeholder={t('declaration.p5.contactPersonName')}
            fieldRule={fieldRequirements['consignee.contactPerson.name']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.contactPersonPhone"
          fieldRule={fieldRequirements['consignee.contactPerson.telephone']}
        >
          <Input
            field={field('contactPerson.telephone')}
            autoComplete={`${scope}.contactPerson.telephone`}
            labelKey="declaration.p5.contactPersonPhone"
            maxLength={35}
            type="text"
            placeholder={t('declaration.p5.contactPersonPhone')}
            fieldRule={fieldRequirements['consignee.contactPerson.telephone']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.contactPersonEmail"
          fieldRule={fieldRequirements['consignee.contactPerson.email']}
        >
          <Input
            field={field('contactPerson.email')}
            autoComplete={`${scope}.contactPerson.email`}
            labelKey="declaration.p5.contactPersonEmail"
            maxLength={256}
            type="text"
            placeholder={t('declaration.p5.contactPersonEmail')}
            fieldRule={fieldRequirements['consignee.contactPerson.email']}
          />
        </FormField>
      </FormCollapsibleColumn>
    </section>
  )
}

Consignee.defaultProps = {
  initialIsSectionOpen: true,
}

export default Consignee
